import { Heading, VStack, HStack, Box, Flex, Text, Icon, Image, SimpleGrid } from "@chakra-ui/react";
import FullScreenSection from "./FullScreenSection";
import { CheckCircleIcon, LockIcon } from "@chakra-ui/icons";
import { FaShieldAlt, FaChartLine, FaGlobe, FaHeart, FaUsers, FaLightbulb } from "react-icons/fa";
import JoinCampaign from "./JoinCampaign";
import Disclosurre from "./disclosurre";

const coreValues = [
  {
    icon: FaHeart,
    color: "red.500",
    title: "Borrower First",
    description: "We design every step of the loan journey around our borrowers, from fast applications to support that is clear, respectful, and responsive."
  },
  {
    icon: FaLightbulb,
    color: "yellow.500",
    title: "Responsible Innovation",
    description: "We use technology to make borrowing faster and simpler while maintaining responsible lending standards that protect long-term financial health."
  },
  {
    icon: FaShieldAlt,
    color: "black.500",
    title: "Trust & Transparency",
    description: "We present rates, terms, and repayment expectations upfront so borrowers can make informed decisions with confidence."
  },
  {
    icon: FaUsers,
    color: "teal.500",
    title: "Financial Progress",
    description: "We support communities by expanding fair access to credit and helping people build stronger financial futures through better borrowing habits."
  }
];



const AboutUs = () => {
  return (
    <VStack spacing={0} width="100%">
      {/* Hero Section - Vision & Mission */}
      <FullScreenSection
        justifyContent="center"
        alignItems="center"
        isDarkBackground={false}
        backgroundImage={`url(${require('../images/landingPageBgFlipped.png')})`}
        backgroundSize="cover"
        backgroundPosition="center center"
        backgroundRepeat="no-repeat"
        py={100}
        overlay={true}
        overlayColor="whiteAlpha.800"
      >
        <VStack spacing={8} maxW="1200px" textAlign="center" zIndex={10}>
          <Heading fontSize={{ base: "2.4rem", md: "3.2rem", lg: "4rem" }} fontWeight="bold" color="black">
            About Swift Credit
          </Heading>
          <Heading fontSize={{ base: "1.4rem", md: "1.8rem", lg: "2rem" }} fontWeight="medium" color="gray.700" maxW="900px">
            Our Vision & Mission
          </Heading>
          <Text fontSize={{ base: "1rem", md: "1.1rem", lg: "1.3rem" }} color="gray.600" maxW="1000px" lineHeight="1.8">
            We're on a mission to make loans accessible and affordable for every Canadian resident, including those with poor and limited credit history. 
            Our vision is to create a world where getting a loan is as quick and simple as making a bank transfer. 
            
          </Text>
          
          {/* Hero Images */}
          <HStack spacing={6} mt={8} flexWrap="wrap" justifyContent="center">
            <Image 
              src="https://images.unsplash.com/photo-1556157382-97eda2d62296?w=300&h=300&fit=crop" 
              alt="Happy customer" 
              boxSize={{ base: "140px", md: "180px", lg: "200px" }} 
              borderRadius="xl" 
              objectFit="cover"
              boxShadow="xl"
            />
            <Image 
              src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=300&h=300&fit=crop" 
              alt="Diverse team" 
              boxSize={{ base: "140px", md: "180px", lg: "200px" }} 
              borderRadius="xl" 
              objectFit="cover"
              boxShadow="xl"
            />
            <Image 
              src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=300&h=300&fit=crop" 
              alt="Customer service" 
              boxSize={{ base: "140px", md: "180px", lg: "200px" }} 
              borderRadius="xl" 
              objectFit="cover"
              boxShadow="xl"
            />
          </HStack>
        </VStack>
      </FullScreenSection>

      {/* The Challenge Section */}
      <FullScreenSection
        justifyContent="center"
        alignItems="center"
        isDarkBackground={false}
        backgroundColor="gray.50"
        py={100}
      >
        <VStack spacing={12} maxW="1200px">
          <VStack spacing={4} textAlign="center">
            <Icon as={FaGlobe} boxSize={{ base: 12, md: 16 }} color="black.500" />
            <Heading fontSize={{ base: "2rem", md: "2.5rem", lg: "3rem" }} fontWeight="bold">
              The Growing Need
            </Heading>
            <Text fontSize={{ base: "1rem", md: "1.1rem", lg: "1.2rem" }} color="gray.600" maxW="900px" lineHeight="1.8">
              Many Canadians still struggle to access fair and timely credit, especially when unexpected expenses appear.
              Banks and traditional lenders can be slow, rigid, and difficult to qualify for, leaving borrowers with limited options when
              they need funds quickly and clear repayment terms they can trust.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10} width="100%">
            <Box p={8} bg="white" borderRadius="xl" boxShadow="lg">
              <Heading fontSize="1.5rem" mb={4} color="red.600">The Problem</Heading>
              <VStack align="flex-start" spacing={3}>
                <HStack>
                  <Box w="8px" h="8px" bg="red.500" borderRadius="full" />
                  <Text color="gray.700">Limited access to credit for borrowers with poor or limited credit history</Text>
                </HStack>
                <HStack>
                  <Box w="8px" h="8px" bg="red.500" borderRadius="full" />
                  <Text color="gray.700">Slow approval processes when funds are needed urgently</Text>
                </HStack>
                <HStack>
                  <Box w="8px" h="8px" bg="red.500" borderRadius="full" />
                  <Text color="gray.700">Confusing terms and unclear total borrowing costs</Text>
                </HStack>
                <HStack>
                  <Box w="8px" h="8px" bg="red.500" borderRadius="full" />
                  <Text color="gray.700">Inflexible repayment options that do not fit real budgets</Text>
                </HStack>
                <HStack>
                  <Box w="8px" h="8px" bg="red.500" borderRadius="full" />
                  <Text color="gray.700">Few trustworthy options that balance speed with responsible lending</Text>
                </HStack>
              </VStack>
            </Box>

            <Box p={8} bg="white" borderRadius="xl" boxShadow="lg">
              <Heading fontSize="1.5rem" mb={4} color="green.600">Our Solution</Heading>
              <VStack align="flex-start" spacing={3}>
                <HStack>
                  <CheckCircleIcon color="green.500" />
                  <Text color="gray.700" fontWeight="semibold">Fast online applications with quick decisions</Text>
                </HStack>
                <HStack>
                  <CheckCircleIcon color="green.500" />
                  <Text color="gray.700" fontWeight="semibold">Intuitive loan calculator and transparent rates</Text>
                </HStack>
                <HStack>
                  <CheckCircleIcon color="green.500" />
                  <Text color="gray.700" fontWeight="semibold">{/*Short-term and payday*/} Loan options tailored to different needs</Text>
                </HStack>
                <HStack>
                  <CheckCircleIcon color="green.500" />
                  <Text color="gray.700" fontWeight="semibold">Transparent and flexible repayment structures designed around affordability</Text>
                </HStack>
                <HStack>
                  <CheckCircleIcon color="green.500" />
                  <Text color="gray.700" fontWeight="semibold">A responsible lending approach that supports credit rebuilding</Text>
                </HStack>
              </VStack>
            </Box>
          </SimpleGrid>
        </VStack>
      </FullScreenSection>

      {/* Zero Fees & Best Value Section */}
      <FullScreenSection
        justifyContent="center"
        alignItems="center"
        isDarkBackground={false}
        backgroundColor="white"
        py={100}
      >
        <VStack spacing={12} maxW="1200px">
          <Heading fontSize={{ base: "2rem", md: "2.5rem", lg: "3rem" }} fontWeight="bold" textAlign="center">
            Rebuild your Credit with Swift Credit
          </Heading>
          
          <Flex gap={12} alignItems="center" flexDirection={{ base: "column", md: "row" }}>
            <Box
              flex="1"
              minH={{ base: "260px", md: "360px" }}
              borderRadius="xl"
              bg="gray.50"
              border="1px solid"
              borderColor="gray.200"
              boxShadow="2xl"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <VStack spacing={4}>
                <Icon as={FaChartLine} boxSize={{ base: 16, md: 20 }} color="black" />
                <Text color="gray.700" fontWeight="semibold" textAlign="center" px={6}>
                  Smart borrowing. Stronger credit.
                </Text>
              </VStack>
            </Box>
            <VStack align="flex-start" spacing={6} flex="1">
              <Box>
                <Heading fontSize={{ base: "1.4rem", md: "1.7rem", lg: "2rem" }} mb={3} color="black">Build Positive Payment History</Heading>
                <Text fontSize={{ base: "0.95rem", md: "1rem", lg: "1.1rem" }} color="gray.600" lineHeight="1.8">
                  Consistent, on-time repayments can help you strengthen your credit profile over time. 
                  Our {/*short-term and payday*/} Loan options are structured to support responsible borrowing with clear terms from the start.
                </Text>
              </Box>
              
              <Box>
                <Heading fontSize={{ base: "1.4rem", md: "1.7rem", lg: "2rem" }} mb={3} color="black">Transparent Terms, Better Control</Heading>
                <Text fontSize={{ base: "0.95rem", md: "1rem", lg: "1.1rem" }} color="gray.600" lineHeight="1.8">
                  We show rates, costs, and repayment expectations upfront so you can borrow with confidence. 
                  With flexible repayment options and a straightforward application process, Swift Credit helps you stay in control while rebuilding credit.
                </Text>
              </Box>
            </VStack>
          </Flex>
        </VStack>
      </FullScreenSection>

      

      
      {/* Core Values Section */}
      <FullScreenSection
        justifyContent="center"
        alignItems="center"
        isDarkBackground={false}
        backgroundColor="white"
        py={100}
      >
        <VStack spacing={12} maxW="1200px">
          <Heading fontSize="3rem" fontWeight="bold" textAlign="center">
            Our Core Values
          </Heading>
          
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={10} width="100%">
            {coreValues.map((value, index) => (
              <VStack
                key={index}
                p={8}
                bg="white"
                borderRadius="xl"
                boxShadow="lg"
                border="2px solid"
                borderColor="gray.200"
                spacing={4}
                transition="all 0.3s ease"
                _hover={{ transform: "translateY(-10px)", boxShadow: "2xl", borderColor: value.color }}
              >
                <Icon as={value.icon} boxSize={16} color={value.color} />
                <Heading fontSize="1.8rem" textAlign="center">
                  {value.title}
                </Heading>
                <Text color="gray.600" textAlign="center" lineHeight="1.8">
                  {value.description}
                </Text>
              </VStack>
            ))}
          </SimpleGrid>
        </VStack>
      </FullScreenSection>

      <Disclosurre />

   
    </VStack>
  );
};

export default AboutUs;
